<template>
  <v-container>
    <v-layout row>
      <v-flex>
        <v-card-text>
          <v-container>

            <div class="page-header">
              <h2>IABF {{ adminFestivalYear }} — Store Orders</h2>
              <div>
                <v-btn outlined class="mr-2" @click="downloadCsv">↓ Orders CSV</v-btn>
                <v-btn outlined class="mr-2" @click="downloadManufacturerCsv">↓ Manufacturer Summary</v-btn>
                <v-btn outlined class="mr-2" @click="downloadPackingCsv">↓ Welcome-Bag Packing List</v-btn>
                <v-btn outlined class="mr-2" @click="openNonOrderedDialog">Who hasn't ordered?</v-btn>
                <v-btn class="mr-2" to="/iabf/admin/store">← Items</v-btn>
              </div>
            </div>

            <div class="summary-bar mb-4">
              <div class="summary-stat">
                <span class="stat-num">{{ paidOrders.length }}</span>
                <span class="stat-label">Paid orders</span>
              </div>
              <div v-if="isFinanceAdmin" class="summary-stat" :title="'Subtotal across all paid orders BEFORE the free-tshirt benefit + welcome credit (the retail value of merch leaving the building).'">
                <span class="stat-num">${{ (paidSubtotalCents / 100).toFixed(0) }}</span>
                <span class="stat-label">Merch value (before discounts)</span>
              </div>
              <div v-if="isFinanceAdmin" class="summary-stat" :title="'Total actually charged through Stripe — what the dancer paid after their free-tshirt benefit + welcome credit + tax.'">
                <span class="stat-num">${{ (paidRevenueCents / 100).toFixed(0) }}</span>
                <span class="stat-label">Stripe revenue (after discounts + tax)</span>
              </div>
              <div class="summary-stat" v-if="freePaidCount > 0" :title="'Orders that came out to \$0 after the dancer benefits — no Stripe charge happened.'">
                <span class="stat-num">{{ freePaidCount }}</span>
                <span class="stat-label">Free orders (\$0 after benefits)</span>
              </div>
              <div class="summary-stat" v-if="pendingOrders.length > 0" :title="'Order docs that were pre-created when the dancer started checkout but never confirmed by the Stripe webhook. Most are abandoned carts.'">
                <span class="stat-num">{{ pendingOrders.length }}</span>
                <span class="stat-label">Pending (abandoned?)</span>
              </div>
              <div class="summary-stat" v-for="g in unitsByType" :key="g.type">
                <span class="stat-num">{{ g.count }}</span>
                <span class="stat-label">{{ g.type }} units</span>
              </div>
            </div>

            <div v-if="ringTally.length" class="ring-tally mb-4">
              <h3>💍 Ring tally (paid)</h3>
              <div class="ring-tally-grid">
                <div v-for="r in ringTally" :key="r.name" class="ring-tally-card">
                  <div class="ring-tally-name">{{ r.name }}</div>
                  <div class="ring-tally-sizes">
                    <span v-for="entry in r.sizes" :key="entry.size" class="ring-tally-pill">
                      <strong>{{ entry.size || '?' }}</strong> × {{ entry.qty }}
                    </span>
                  </div>
                  <div class="ring-tally-total">{{ r.total }} ordered · {{ Math.max(0, 12 - r.total) }} more to hit 12-min</div>
                </div>
              </div>
            </div>

            <div v-if="sizesByItem.length" class="size-breakdown mb-4">
              <h3>Size Breakdown (paid orders)</h3>
              <table class="size-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th v-for="s in sizeOptions" :key="s">{{ s }}</th>
                    <th>No size</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in sizesByItem" :key="row.itemName">
                    <td><strong>{{ row.itemName }}</strong></td>
                    <td v-for="s in sizeOptions" :key="s">{{ row.sizes[s] || '—' }}</td>
                    <td>{{ row.sizes[''] || '—' }}</td>
                    <td><strong>{{ row.total }}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="loading" class="muted">Loading…</div>

            <table v-else class="orders-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Type</th>
                  <th>Dancer</th>
                  <th>Items</th>
                  <th>Subtotal</th>
                  <th>Discounts</th>
                  <th>Tax</th>
                  <th>Ship</th>
                  <th>Total</th>
                  <th>Placed</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="o in sortedOrders" :key="o.id">
                  <td>
                    <span class="status" :class="'status-' + o.status">{{ o.status }}</span>
                  </td>
                  <td>
                    <span v-if="o.isFaculty" class="type-tag type-faculty">Faculty</span>
                    <span v-else-if="o.isGuest" class="type-tag type-guest">Guest</span>
                    <span v-else class="type-tag type-dancer">Dancer</span>
                  </td>
                  <td>
                    <div>{{ o.dancerName }}</div>
                    <div class="sub">{{ o.dancerEmail }}</div>
                    <div v-if="o.shippingAddress && o.shippingAddress.street" class="ship-addr">
                      📦 {{ formatShipAddress(o.shippingAddress) }}
                    </div>
                  </td>
                  <td>
                    <div v-for="(line, i) in o.lines" :key="i" class="order-line" :class="{ 'order-line-addon': line.addedAfterCheckout }">
                      <span v-if="line.addedAfterCheckout" class="addon-tag" title="Added after original checkout">+</span>
                      {{ line.quantity }} × {{ line.name }}<span v-if="line.size"> ({{ line.size }})</span>
                    </div>
                  </td>
                  <td>${{ ((o.subtotalCents || 0) / 100).toFixed(2) }}</td>
                  <td>
                    <div v-if="o.freeBenefitCents > 0">−${{ ((o.freeBenefitCents || 0) / 100).toFixed(2) }} ({{ o.freeBenefitKind }})</div>
                    <div v-if="o.merchCreditUsedCents > 0">−${{ ((o.merchCreditUsedCents || 0) / 100).toFixed(2) }} (credit)</div>
                  </td>
                  <td>${{ ((o.taxCents || 0) / 100).toFixed(2) }}</td>
                  <td>${{ ((o.shippingCents || 0) / 100).toFixed(2) }}</td>
                  <td><strong>${{ ((o.totalCents || 0) / 100).toFixed(2) }}</strong></td>
                  <td>{{ formatDate(o.createdAt) }}</td>
                  <td class="row-actions">
                    <v-btn v-if="o.status !== 'paid'" x-small outlined color="success" :loading="markingPaid[o.id]" @click="markPaid(o)">Mark paid</v-btn>
                    <v-btn v-if="o.status === 'paid' && o.dancerId" x-small outlined color="primary" @click="openAddItem(o)">+ Add item</v-btn>
                    <v-btn x-small text color="error" @click="confirmDelete(o)">Delete</v-btn>
                  </td>
                </tr>
              </tbody>
            </table>

            <v-dialog v-model="showDeleteDialog" max-width="460">
              <v-card v-if="orderToDelete">
                <v-card-title>Delete this order?</v-card-title>
                <v-card-text>
                  <p><strong>{{ orderToDelete.dancerName }}</strong> · <span class="muted">{{ orderToDelete.dancerEmail }}</span></p>
                  <ul class="delete-lines">
                    <li v-for="(l, i) in (orderToDelete.lines || [])" :key="i">
                      {{ l.quantity }}× {{ l.name }}<span v-if="l.size"> ({{ l.size }})</span>
                    </li>
                  </ul>
                  <p class="muted small">Total: ${{ ((orderToDelete.totalCents || 0) / 100).toFixed(2) }} · Placed {{ formatDate(orderToDelete.createdAt) }}</p>
                  <p class="warn"><strong>⚠ This permanently deletes the Firestore order doc.</strong> If the order was paid through Stripe, the charge in Stripe is NOT refunded — issue any refund in the Stripe dashboard separately.</p>
                </v-card-text>
                <v-card-actions>
                  <v-btn text @click="closeDeleteDialog">Cancel</v-btn>
                  <v-spacer />
                  <v-btn color="error" dark :loading="deleting" @click="runDelete">Delete order</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <v-dialog v-model="showNonOrderedDialog" max-width="720" scrollable>
              <v-card>
                <v-card-title>Hasn't ordered yet</v-card-title>
                <v-card-text style="max-height: 70vh;">
                  <p class="muted small" v-if="!nonOrderedLoaded">Loading…</p>
                  <template v-else>
                    <div class="non-ordered-summary">
                      <span class="type-tag type-dancer">Dancer</span>
                      <strong>{{ dancersNotOrdered.length }}</strong> dancers haven't ordered ·
                      <span class="type-tag type-faculty">Faculty</span>
                      <strong>{{ facultyNotOrdered.length }}</strong> faculty haven't ordered
                    </div>

                    <div class="copy-row">
                      <v-btn small outlined @click="copyDancerEmails">
                        {{ dancerCopyNote || `Copy ${dancersNotOrdered.length} dancer email${dancersNotOrdered.length === 1 ? '' : 's'}` }}
                      </v-btn>
                      <v-btn small outlined @click="downloadNonOrderedCsv">↓ Full CSV</v-btn>
                    </div>

                    <h4 class="section-h">Dancers</h4>
                    <table class="non-ordered-table">
                      <thead><tr><th>Name</th><th>Email</th><th></th></tr></thead>
                      <tbody>
                        <tr v-for="d in dancersNotOrdered" :key="d.id">
                          <td>{{ d.firstName }} {{ d.lastName }}</td>
                          <td class="email-cell">{{ d.email }}</td>
                          <td><v-btn x-small outlined @click="openPlaceOrder(d)">Place order</v-btn></td>
                        </tr>
                        <tr v-if="dancersNotOrdered.length === 0"><td colspan="3" class="muted">All dancers have ordered 🎉</td></tr>
                      </tbody>
                    </table>

                    <h4 class="section-h">Faculty <span class="muted small">(matched against the meal guest list)</span></h4>
                    <table class="non-ordered-table">
                      <thead><tr><th>Name</th><th>Email (if any)</th></tr></thead>
                      <tbody>
                        <tr v-for="f in facultyNotOrdered" :key="f.id">
                          <td>{{ f.guestName }}</td>
                          <td class="email-cell">{{ f.email || '—' }}</td>
                        </tr>
                        <tr v-if="facultyNotOrdered.length === 0"><td colspan="2" class="muted">All faculty on the meal list have ordered 🎉</td></tr>
                      </tbody>
                    </table>
                  </template>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn text @click="showNonOrderedDialog = false">Close</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- Place order on behalf of a dancer (free t-shirt) -->
            <v-dialog v-model="showPlaceOrderDialog" max-width="520">
              <v-card v-if="placeOrderDancer">
                <v-card-title>Place free t-shirt order — {{ placeOrderDancer.firstName }} {{ placeOrderDancer.lastName }}</v-card-title>
                <v-card-text>
                  <p class="muted small">{{ placeOrderDancer.email }}</p>
                  <div class="po-row">
                    <label class="po-lab">T-shirt</label>
                    <select v-model="placeOrderItemId" class="po-input">
                      <option value="">— pick a style —</option>
                      <option v-for="i in tshirtItems" :key="i.id" :value="i.id">{{ i.name }}</option>
                    </select>
                  </div>
                  <div class="po-row">
                    <label class="po-lab">
                      Size
                      <span v-if="suggestedSizeLabel" class="po-suggest">(suggested: {{ suggestedSizeLabel }})</span>
                    </label>
                    <select v-model="placeOrderSize" class="po-input">
                      <option value="">— pick a size —</option>
                      <option v-for="s in sizeOptions" :key="s" :value="s">{{ s }}</option>
                    </select>
                  </div>
                  <p v-if="placeOrderError" class="error--text small">{{ placeOrderError }}</p>
                </v-card-text>
                <v-card-actions>
                  <v-btn text @click="showPlaceOrderDialog = false" :disabled="placeOrderSaving">Cancel</v-btn>
                  <v-spacer />
                  <v-btn color="primary" :loading="placeOrderSaving" :disabled="!placeOrderItemId || !placeOrderSize" @click="submitPlaceOrder">Place free order</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <!-- Add item to existing paid order and charge via Stripe -->
            <v-dialog v-model="showAddItemDialog" max-width="540">
              <v-card v-if="addItemOrder">
                <v-card-title>Add merch & charge — {{ addItemOrder.dancerName }}</v-card-title>
                <v-card-text>
                  <p class="muted small">
                    Charges the dancer's saved card on file. No benefits apply (free t-shirt / merch credit
                    were one-shot deals at original checkout). Stripe sends them a receipt automatically.
                  </p>
                  <div class="po-row">
                    <label class="po-lab">Item</label>
                    <select v-model="addItemItemId" class="po-input">
                      <option value="">— pick an item —</option>
                      <option v-for="i in addableItems" :key="i.id" :value="i.id">
                        {{ i.name }} (${{ ((i.priceCents || 0) / 100).toFixed(2) }})
                      </option>
                    </select>
                  </div>
                  <div v-if="addItemRequiresSize" class="po-row">
                    <label class="po-lab">Size</label>
                    <select v-model="addItemSize" class="po-input">
                      <option value="">— pick a size —</option>
                      <option v-for="s in sizeOptions" :key="s" :value="s">{{ s }}</option>
                    </select>
                  </div>
                  <div class="po-row">
                    <label class="po-lab">Quantity</label>
                    <input type="number" min="1" max="20" v-model.number="addItemQty" class="po-input" />
                  </div>
                  <div v-if="addItemItem" class="addon-summary">
                    <div>Item: ${{ (addItemSubtotalCents / 100).toFixed(2) }}</div>
                    <div>Tax (6.5%): ${{ (addItemTaxCents / 100).toFixed(2) }}</div>
                    <div class="addon-total"><strong>Total charge: ${{ (addItemTotalCents / 100).toFixed(2) }}</strong></div>
                  </div>
                  <p v-if="addItemError" class="error--text small">{{ addItemError }}</p>
                  <p v-if="addItemSuccess" class="success-msg small">{{ addItemSuccess }}</p>
                </v-card-text>
                <v-card-actions>
                  <v-btn text @click="showAddItemDialog = false" :disabled="addItemSaving">Close</v-btn>
                  <v-spacer />
                  <v-btn color="primary" :loading="addItemSaving" :disabled="!canSubmitAddItem" @click="submitAddItem">
                    Charge ${{ (addItemTotalCents / 100).toFixed(2) }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

          </v-container>
        </v-card-text>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { db, functions } from '@/firebase'
import iabfAdminMixin from '@/utils/iabf-admin-mixin'
import { SIZE_OPTIONS } from '@/utils/store'


export default {
  mixins: [iabfAdminMixin],
  data () {
    return {
      loading: true,
      orders: [],
      sizeOptions: SIZE_OPTIONS,
      showDeleteDialog: false,
      orderToDelete: null,
      deleting: false,
      showNonOrderedDialog: false,
      nonOrderedLoaded: false,
      allDancers: [],
      allFaculty: [],
      allItems: [],
      dancerCopyNote: '',
      markingPaid: {},
      showPlaceOrderDialog: false,
      placeOrderDancer: null,
      placeOrderItemId: '',
      placeOrderSize: '',
      placeOrderSaving: false,
      placeOrderError: '',
      // Add-item-and-charge dialog (off-session Stripe charge using
      // the dancer's saved card; line is appended to the order).
      showAddItemDialog: false,
      addItemOrder: null,
      addItemItemId: '',
      addItemSize: '',
      addItemQty: 1,
      addItemSaving: false,
      addItemError: '',
      addItemSuccess: ''
    }
  },
  computed: {
    isFinanceAdmin () {
      return this.$store.getters.isFinanceAdmin
    },
    sortedOrders () {
      return this.orders.slice().sort((a, b) => {
        const ta = a.createdAt && a.createdAt.toMillis ? a.createdAt.toMillis() : 0
        const tb = b.createdAt && b.createdAt.toMillis ? b.createdAt.toMillis() : 0
        return tb - ta
      })
    },
    paidOrders () {
      return this.orders.filter(o => o.status === 'paid')
    },
    orderedEmailSet () {
      const set = new Set()
      for (const o of this.paidOrders) {
        const e = (o.dancerEmail || '').toLowerCase().trim()
        if (e) set.add(e)
      }
      return set
    },
    orderedNameSet () {
      // Lower-cased normalized name → for faculty matching where email is
      // typically not on the meal-list record.
      const set = new Set()
      for (const o of this.paidOrders) {
        const n = (o.dancerName || '').toLowerCase().replace(/\s+/g, ' ').trim()
        if (n) set.add(n)
      }
      return set
    },
    dancersNotOrdered () {
      return this.allDancers
        .filter(d => {
          const email = (d.email || '').toLowerCase().trim()
          return email && !this.orderedEmailSet.has(email)
        })
        .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''))
    },
    facultyOrderFirstNameSet () {
      // First names from orders flagged isFaculty at checkout. Lets us
      // match faculty meal-list entries (e.g. "Nina Geromel") against an
      // order whose dancerName was just "Nina" — useful when faculty type
      // a short name at checkout and the meal list has the full name.
      const set = new Set()
      for (const o of this.paidOrders) {
        if (!o.isFaculty) continue
        const n = (o.dancerName || '').toLowerCase().replace(/\s+/g, ' ').trim()
        if (!n) continue
        const first = n.split(/\s+/)[0]
        if (first) set.add(first)
      }
      return set
    },
    facultyNotOrdered () {
      return this.allFaculty
        .filter(f => {
          const name = (f.guestName || '').toLowerCase().replace(/\s+/g, ' ').trim()
          const email = (f.email || '').toLowerCase().trim()
          const first = name.split(/\s+/)[0] || ''
          // Match by full name, email, OR a first-name match against any
          // order already flagged isFaculty (so "Nina Geromel" on the meal
          // list doesn't show as unordered when an order with dancerName
          // "Nina" + isFaculty already exists).
          if (name && this.orderedNameSet.has(name)) return false
          if (email && this.orderedEmailSet.has(email)) return false
          if (first && this.facultyOrderFirstNameSet.has(first)) return false
          return !!name
        })
        .sort((a, b) => (a.guestName || '').localeCompare(b.guestName || ''))
    },
    tshirtItems () {
      return this.allItems
        .filter(i => i.type === 'tshirt')
        .slice()
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    },
    addableItems () {
      return this.allItems
        .filter(i => i.published && (Number(i.priceCents) || 0) > 0)
        .slice()
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    },
    addItemItem () {
      return this.allItems.find(i => i.id === this.addItemItemId) || null
    },
    addItemRequiresSize () {
      const t = (this.addItemItem || {}).type
      return ['tshirt', 'cozy', 'ring'].includes(t)
    },
    addItemUnitPriceCents () {
      return Number((this.addItemItem || {}).priceCents) || 0
    },
    addItemSubtotalCents () {
      return this.addItemUnitPriceCents * Math.max(1, this.addItemQty || 1)
    },
    addItemTaxCents () {
      // FL sales tax removed post-festival — mirrors STORE_TAX_RATE = 0 on the server.
      return 0
    },
    addItemTotalCents () {
      return this.addItemSubtotalCents + this.addItemTaxCents
    },
    canSubmitAddItem () {
      if (!this.addItemOrder || !this.addItemItem) return false
      if (this.addItemRequiresSize && !this.addItemSize) return false
      if (this.addItemSaving) return false
      return this.addItemUnitPriceCents > 0
    },
    suggestedSizeLabel () {
      if (!this.placeOrderDancer) return ''
      const m = this.placeOrderDancer.sharedMeasurements || {}
      // The dancers' costume form captures bust (or chest, same field) in
      // inches. Map to the District women's chart used across the store —
      // same chart admin charts assume for tees + tanks. Fall back to ''
      // when the dancer hasn't filled in their bust.
      const bust = parseFloat(m.bust)
      if (!bust || isNaN(bust)) return ''
      if (bust < 35) return 'XS'
      if (bust < 37) return 'S'
      if (bust < 39) return 'M'
      if (bust < 42) return 'L'
      if (bust < 45) return 'XL'
      return '2XL'
    },
    pendingOrders () {
      return this.orders.filter(o => o.status !== 'paid' && o.status !== 'cancelled')
    },
    paidRevenueCents () {
      return this.paidOrders.reduce((s, o) => s + (o.totalCents || 0), 0)
    },
    paidSubtotalCents () {
      return this.paidOrders.reduce((s, o) => s + (o.subtotalCents || 0), 0)
    },
    freePaidCount () {
      return this.paidOrders.filter(o => (o.totalCents || 0) === 0).length
    },
    unitsByType () {
      const counts = {}
      for (const o of this.paidOrders) {
        for (const line of (o.lines || [])) {
          const t = line.type || 'item'
          counts[t] = (counts[t] || 0) + (line.quantity || 0)
        }
      }
      return Object.keys(counts).sort().map(type => ({ type, count: counts[type] }))
    },
    ringTally () {
      // Compact per-ring breakdown of which sizes are already spoken for.
      // Used to decide how many extras of which sizes to order to hit the
      // 12-per-color manufacturer minimum.
      const byRing = {}
      for (const o of this.paidOrders) {
        for (const line of (o.lines || [])) {
          if ((line.type || '').toLowerCase() !== 'ring') continue
          const key = line.name || '(unnamed ring)'
          if (!byRing[key]) byRing[key] = { name: key, sizes: {}, total: 0 }
          const size = (line.size || '').trim()
          byRing[key].sizes[size] = (byRing[key].sizes[size] || 0) + (line.quantity || 0)
          byRing[key].total += (line.quantity || 0)
        }
      }
      // Convert sizes object → array sorted numerically (then alphabetically
      // for non-numeric values), so "6", "7", "9", "?" appear in order.
      const numericSort = (a, b) => {
        const na = parseFloat(a.size)
        const nb = parseFloat(b.size)
        if (!isNaN(na) && !isNaN(nb)) return na - nb
        if (!isNaN(na)) return -1
        if (!isNaN(nb)) return 1
        return String(a.size).localeCompare(String(b.size))
      }
      return Object.values(byRing).map(r => ({
        ...r,
        sizes: Object.entries(r.sizes).map(([size, qty]) => ({ size, qty })).sort(numericSort)
      })).sort((a, b) => a.name.localeCompare(b.name))
    },
    sizesByItem () {
      // One row per SKU (full item name including color) with sizes as
      // columns so the manufacturer sees how many of each color × size
      // to produce.
      const byItem = {}
      for (const o of this.paidOrders) {
        for (const line of (o.lines || [])) {
          const key = line.name || '(unnamed)'
          if (!byItem[key]) byItem[key] = { itemName: key, sizes: {}, total: 0 }
          const size = line.size || ''
          byItem[key].sizes[size] = (byItem[key].sizes[size] || 0) + (line.quantity || 0)
          byItem[key].total += (line.quantity || 0)
        }
      }
      return Object.values(byItem).sort((a, b) => a.itemName.localeCompare(b.itemName))
    }
  },
  async created () {
    const snap = await db.collection(this.IABF.storeOrders).get()
    this.orders = snap.docs.map(d => ({ id: d.id, ...d.data() }))
    this.loading = false
  },
  methods: {
    formatDate (ts) {
      if (!ts) return ''
      const d = ts.toDate ? ts.toDate() : ts
      if (!(d instanceof Date)) return ''
      return d.toISOString().slice(0, 10)
    },
    formatShipAddress (a) {
      if (!a) return ''
      const line1 = [a.street, a.street2].filter(Boolean).join(', ')
      const cityLine = [a.city, [a.state, a.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')
      return [line1, cityLine].filter(Boolean).join(' · ')
    },
    downloadCsv () {
      const headers = ['Status', 'Type', 'Name', 'Email', 'Ship to', 'Items', 'Subtotal', 'Free benefit', 'Merch credit', 'Tax', 'Shipping', 'Total', 'Placed']
      const rows = this.sortedOrders.map(o => [
        o.status,
        o.isGuest ? 'Guest' : 'Dancer',
        o.dancerName,
        o.dancerEmail,
        this.formatShipAddress(o.shippingAddress),
        (o.lines || []).map(l => `${l.quantity}x ${l.name}${l.size ? ' (' + l.size + ')' : ''}`).join(' · '),
        ((o.subtotalCents || 0) / 100).toFixed(2),
        ((o.freeBenefitCents || 0) / 100).toFixed(2),
        ((o.merchCreditUsedCents || 0) / 100).toFixed(2),
        ((o.taxCents || 0) / 100).toFixed(2),
        ((o.shippingCents || 0) / 100).toFixed(2),
        ((o.totalCents || 0) / 100).toFixed(2),
        this.formatDate(o.createdAt)
      ])
      this.triggerCsvDownload('iabf-2026-store-orders.csv', headers, rows)
    },
    triggerCsvDownload (filename, headers, rows) {
      const escape = v => {
        const s = String(v == null ? '' : v)
        return (s.includes(',') || s.includes('"') || s.includes('\n')) ? '"' + s.replace(/"/g, '""') + '"' : s
      }
      const csv = [headers, ...rows].map(r => r.map(escape).join(',')).join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    },
    downloadManufacturerCsv () {
      // Pivot: one row per SKU (item name including color/variant) with
      // sizes as columns, so the manufacturer sees how many of each
      // color × size to produce.
      const byItem = {}
      for (const o of this.paidOrders) {
        for (const l of (o.lines || [])) {
          const key = l.name || '(unnamed)'
          if (!byItem[key]) byItem[key] = { name: key, sizes: {}, total: 0 }
          const size = l.size || ''
          byItem[key].sizes[size] = (byItem[key].sizes[size] || 0) + (l.quantity || 0)
          byItem[key].total += (l.quantity || 0)
        }
      }
      const items = Object.values(byItem).sort((a, b) => a.name.localeCompare(b.name))
      const headers = ['Item', ...this.sizeOptions, 'No size', 'Total']
      const rows = items.map(it => [
        it.name,
        ...this.sizeOptions.map(s => it.sizes[s] || ''),
        it.sizes[''] || '',
        it.total
      ])
      this.triggerCsvDownload('iabf-2026-manufacturer-summary.csv', headers, rows)
    },
    downloadPackingCsv () {
      // One row per person per item. Sized items repeat the line per
      // copy so each physical garment gets its own pack line; multi-
      // quantity items expand into N rows. Sorted by recipient name then
      // by item, so it's easy to bag person-by-person.
      const rows = []
      const sortedOrders = this.paidOrders
        .slice()
        .sort((a, b) => (a.dancerName || '').localeCompare(b.dancerName || ''))
      for (const o of sortedOrders) {
        const type = o.isFaculty ? 'Faculty' : (o.isGuest ? 'Guest' : 'Dancer')
        const lines = (o.lines || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        for (const l of lines) {
          const qty = Math.max(1, l.quantity || 1)
          for (let i = 0; i < qty; i++) {
            rows.push([
              o.dancerName || '',
              o.dancerEmail || '',
              type,
              l.name || '',
              l.size || '',
              this.formatDate(o.paidAt || o.createdAt)
            ])
          }
        }
      }
      const headers = ['Name', 'Email', 'Type', 'Item', 'Size', 'Order Date']
      this.triggerCsvDownload('iabf-2026-welcome-bag-packing.csv', headers, rows)
    },
    async markPaid (order) {
      // Manual fallback for the Stripe webhook — does the same writes
      // stripeWebhookStoreOrder would do on checkout.session.completed:
      //   1. order.status → 'paid' + paidAt timestamp
      //   2. dancer.merchFreeBenefitUsed → true (if benefit applied)
      //   3. dancer.merchCreditUsedCents += merchCreditUsedCents
      // Safe to re-run; benefit flags are idempotent.
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Mark this order as paid?\n\n${order.dancerName} · $${((order.totalCents || 0) / 100).toFixed(2)}\n\nThis assumes the Stripe charge succeeded. Verify in the Stripe dashboard first if you're not sure.`)) return
      this.$set(this.markingPaid, order.id, true)
      try {
        const firestore = (await import('@/firebase')).db
        const FieldValue = (await import('firebase/app')).default ? null : null
        // Use the simpler approach: read-then-write the dancer doc.
        await firestore.collection(this.IABF.storeOrders).doc(order.id).update({
          status: 'paid',
          paidAt: new Date()
        })
        if (order.dancerId) {
          const updates = {}
          if ((order.freeBenefitCents || 0) > 0) updates.merchFreeBenefitUsed = true
          if ((order.merchCreditUsedCents || 0) > 0) {
            // Read current value, add the credit used. (Avoids FieldValue
            // import gymnastics across our two Firebase SDK versions.)
            const dancerSnap = await firestore.collection(this.IABF.dancers).doc(order.dancerId).get()
            const prev = Number((dancerSnap.data() || {}).merchCreditUsedCents) || 0
            updates.merchCreditUsedCents = prev + order.merchCreditUsedCents
          }
          if (Object.keys(updates).length) {
            await firestore.collection(this.IABF.dancers).doc(order.dancerId).update(updates)
          }
        }
        // Reflect locally without a full reload.
        const o = this.orders.find(x => x.id === order.id)
        if (o) { o.status = 'paid'; o.paidAt = new Date() }
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert('Mark-paid failed: ' + (e.message || e))
      } finally {
        this.$set(this.markingPaid, order.id, false)
      }
    },
    confirmDelete (order) {
      this.orderToDelete = order
      this.showDeleteDialog = true
    },
    closeDeleteDialog () {
      this.showDeleteDialog = false
      this.orderToDelete = null
    },
    async runDelete () {
      if (!this.orderToDelete) return
      this.deleting = true
      try {
        await db.collection(this.IABF.storeOrders).doc(this.orderToDelete.id).delete()
        this.orders = this.orders.filter(o => o.id !== this.orderToDelete.id)
        this.closeDeleteDialog()
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert('Delete failed: ' + (e.message || e))
      } finally {
        this.deleting = false
      }
    },
    async ensureItemsLoaded () {
      if (this.allItems.length > 0) return
      const snap = await db.collection(this.IABF.storeItems).get()
      this.allItems = snap.docs.map(d => ({ id: d.id, ...d.data() }))
    },
    async openNonOrderedDialog () {
      this.showNonOrderedDialog = true
      if (this.nonOrderedLoaded) return
      try {
        const [dancerSnap, guestSnap] = await Promise.all([
          db.collection(this.IABF.dancers).get(),
          db.collection(this.IABF.guestRegistrations).where('type', '==', 'faculty').get(),
          this.ensureItemsLoaded()
        ])
        this.allDancers = dancerSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        this.allFaculty = guestSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        this.nonOrderedLoaded = true
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert('Failed to load people: ' + (e.message || e))
      }
    },
    async openAddItem (order) {
      this.addItemOrder = order
      this.addItemItemId = ''
      this.addItemSize = ''
      this.addItemQty = 1
      this.addItemError = ''
      this.addItemSuccess = ''
      this.showAddItemDialog = true
      try {
        await this.ensureItemsLoaded()
      } catch (e) {
        this.addItemError = 'Failed to load items: ' + (e.message || e)
      }
    },
    async submitAddItem () {
      if (!this.canSubmitAddItem) return
      this.addItemError = ''
      this.addItemSuccess = ''
      this.addItemSaving = true
      try {
        const callable = functions.httpsCallable('addStoreOrderItemAndCharge')
        const res = await callable({
          orderId: this.addItemOrder.id,
          itemId: this.addItemItemId,
          size: this.addItemSize || '',
          quantity: this.addItemQty || 1
        })
        const result = res && res.data ? res.data : {}
        // Reflect locally without a full reload: append the new line
        // and bump the running totals. Mirrors what the server wrote.
        const o = this.orders.find(x => x.id === this.addItemOrder.id)
        if (o && result.line) {
          o.lines = [...(o.lines || []), result.line]
          const itemSubtotal = (result.line.unitPriceCents || 0) * (result.line.quantity || 0)
          o.subtotalCents = (o.subtotalCents || 0) + itemSubtotal
          o.totalCents = (o.totalCents || 0) + (result.amountCents || 0)
        }
        const dollars = ((result.amountCents || 0) / 100).toFixed(2)
        this.addItemSuccess = `Charged $${dollars}. ${result.receiptUrl ? 'Stripe receipt emailed to dancer.' : ''}`
        // Reset the picker so a second add starts fresh.
        this.addItemItemId = ''
        this.addItemSize = ''
        this.addItemQty = 1
      } catch (e) {
        this.addItemError = (e && e.message) || 'Charge failed'
      }
      this.addItemSaving = false
    },
    openPlaceOrder (dancer) {
      this.placeOrderDancer = dancer
      this.placeOrderItemId = ''
      this.placeOrderSize = this.suggestedSizeLabel || ''
      this.placeOrderError = ''
      this.showPlaceOrderDialog = true
    },
    async submitPlaceOrder () {
      const dancer = this.placeOrderDancer
      const item = this.tshirtItems.find(i => i.id === this.placeOrderItemId)
      if (!dancer || !item || !this.placeOrderSize) return
      this.placeOrderError = ''
      this.placeOrderSaving = true
      try {
        const unitPriceCents = item.priceCents || 0
        const line = {
          itemId: item.id,
          name: item.name || '',
          type: item.type || 'tshirt',
          unitPriceCents,
          quantity: 1,
          size: this.placeOrderSize,
          photoUrl: item.photoUrl || ''
        }
        // Marked paid because the admin is placing it on the dancer's
        // behalf; this is the same free-tshirt benefit dancers get when
        // they check out themselves, so no Stripe session is needed.
        const order = {
          dancerId: dancer.id,
          dancerName: `${dancer.firstName || ''} ${dancer.lastName || ''}`.trim(),
          dancerEmail: (dancer.email || '').toLowerCase().trim(),
          isGuest: false,
          isFaculty: false,
          lines: [line],
          subtotalCents: unitPriceCents,
          freeBenefitCents: unitPriceCents,
          freeBenefitKind: 'tshirt',
          freeBenefitLineKey: item.id,
          totalCents: 0,
          status: 'paid',
          source: 'admin-placed',
          createdAt: new Date(),
          paidAt: new Date()
        }
        const ref = await db.collection(this.IABF.storeOrders).add(order)
        // Push into local orders state so the dancer drops off the
        // not-ordered list immediately, no reload needed.
        this.orders.push({ id: ref.id, ...order })
        this.showPlaceOrderDialog = false
      } catch (e) {
        this.placeOrderError = e.message || 'Failed to place order'
      }
      this.placeOrderSaving = false
    },
    async copyDancerEmails () {
      const emails = this.dancersNotOrdered.map(d => d.email).filter(Boolean)
      try {
        await navigator.clipboard.writeText(emails.join(', '))
        this.dancerCopyNote = `✓ Copied ${emails.length}`
      } catch (e) {
        this.dancerCopyNote = 'Copy failed'
      }
      setTimeout(() => { this.dancerCopyNote = '' }, 2000)
    },
    downloadNonOrderedCsv () {
      const headers = ['Type', 'Name', 'Email']
      const rows = [
        ...this.dancersNotOrdered.map(d => ['Dancer', `${d.firstName || ''} ${d.lastName || ''}`.trim(), d.email || '']),
        ...this.facultyNotOrdered.map(f => ['Faculty', f.guestName || '', f.email || ''])
      ]
      this.triggerCsvDownload('iabf-2026-hasnt-ordered.csv', headers, rows)
    }
  }
}
</script>

<style scoped>
h2 { font-family: Quattrocento, serif; font-weight: 400; font-size: 28px; margin: 0; }
.type-tag { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
.type-dancer { background: #e8f0fb; color: #2c5282; }
.type-faculty { background: #fef3c7; color: #92400e; }
.type-guest { background: #ece9e2; color: #555; }
.delete-lines { padding-left: 18px; margin: 6px 0 10px 0; font-size: 14px; }
.warn { background: #fef3c7; color: #92400e; padding: 10px 12px; border-radius: 6px; font-size: 13px; margin-top: 12px; }
.non-ordered-summary { background: #faf8f3; padding: 10px 14px; border-radius: 6px; margin-bottom: 12px; display: flex; gap: 10px; align-items: center; flex-wrap: wrap; font-size: 14px; }

.ring-tally h3 { font-family: Quattrocento, Georgia, serif; font-weight: 400; font-size: 18px; margin: 0 0 10px; }
.ring-tally-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 10px; }
.ring-tally-card { background: #faf8f3; border: 1px solid #ece5d2; border-radius: 6px; padding: 12px 14px; }
.ring-tally-name { font-family: Quattrocento, Georgia, serif; font-weight: 700; font-size: 14px; color: #1a1a1a; margin-bottom: 8px; }
.ring-tally-sizes { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
.ring-tally-pill { background: #fff; border: 1px solid #ddd; border-radius: 999px; padding: 3px 10px; font-family: 'Fanwood Text', Georgia, serif; font-size: 13px; color: #1a1a1a; }
.ring-tally-pill strong { color: #1a4452; }
.ring-tally-total { font-family: 'Fanwood Text', Georgia, serif; font-size: 12px; color: #666; font-style: italic; }
.po-row { margin-top: 14px; }
.po-lab { display: block; font-family: 'Fanwood Text', Georgia, serif; font-weight: 700; font-size: 13px; color: #555; margin-bottom: 4px; }
.po-suggest { font-weight: 400; color: #267E7D; font-style: italic; margin-left: 6px; }
.po-input { width: 100%; padding: 8px 10px; border: 1px solid #ccc; border-radius: 4px; font-family: 'Fanwood Text', Georgia, serif; font-size: 14px; background: #fff; }
.po-input:focus { outline: none; border-color: #267E7D; }
.copy-row { display: flex; gap: 10px; margin-bottom: 16px; }
.section-h { font-family: Quattrocento, serif; font-weight: 700; font-size: 16px; margin: 16px 0 6px 0; }
.non-ordered-table { width: 100%; border-collapse: collapse; font-size: 14px; margin-bottom: 8px; }
.non-ordered-table th, .non-ordered-table td { padding: 6px 10px; text-align: left; border-bottom: 1px solid #f3f1ed; }
.non-ordered-table th { color: #888; font-weight: 600; }
.email-cell { color: #555; }
.row-actions { display: flex; gap: 4px; align-items: center; white-space: nowrap; }
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.muted { color: #888; font-style: italic; padding: 40px 0; text-align: center; }
.summary-bar { display: flex; gap: 16px; flex-wrap: wrap; }
.summary-stat { display: flex; flex-direction: column; align-items: center; background: #f5f3f0; border-radius: 6px; padding: 10px 18px; min-width: 90px; }
.stat-num { font-family: Quattrocento, serif; font-size: 26px; font-weight: 700; color: #1a1a1a; }
.stat-label { font-size: 12px; color: #888; margin-top: 2px; text-align: center; }
.size-breakdown { background: #fff; border: 1px solid #eee; border-radius: 6px; padding: 12px 16px; }
.size-breakdown h3 { font-family: Quattrocento, serif; font-weight: 700; font-size: 16px; margin: 0 0 8px 0; }
.size-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.size-table th, .size-table td { padding: 6px 10px; text-align: left; border-bottom: 1px solid #f3f1ed; }
.size-table th { font-size: 11px; color: #999; text-transform: uppercase; }
.orders-table { width: 100%; border-collapse: collapse; font-size: 14px; margin-top: 12px; }
.orders-table th { text-align: left; padding: 8px; font-size: 11px; text-transform: uppercase; color: #999; border-bottom: 1px solid #eee; }
.orders-table td { padding: 10px 8px; border-bottom: 1px solid #f3f1ed; vertical-align: top; }
.order-line { font-size: 13px; color: #1a1a1a; }
.order-line-addon { color: #267E7D; }
.addon-tag {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  background: #267E7D;
  color: #fff;
  padding: 0 5px;
  border-radius: 8px;
  margin-right: 4px;
  vertical-align: middle;
  cursor: help;
}
.addon-summary {
  margin-top: 14px;
  padding: 10px 12px;
  background: #f5f3f0;
  border-radius: 4px;
  font-size: 13px;
}
.addon-summary > div { margin: 2px 0; }
.addon-total { margin-top: 6px !important; font-size: 14px; }
.success-msg { color: #267E7D; margin-top: 8px; }
.sub { color: #888; font-size: 12px; }
.ship-addr { color: #267E7D; font-size: 12px; margin-top: 3px; max-width: 240px; line-height: 1.35; }
.status { font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 10px; }
.status-paid { background: #e8f5e9; color: #1b5e20; }
.status-pending { background: #fff3e0; color: #b26a00; }
.status-created { background: #e3f2fd; color: #0d47a1; }
.status-cancelled { background: #fafafa; color: #888; }
</style>
